if (!customElements.get("m-trust-section")) {
  class MTrustSection extends HTMLElement {
    constructor() {
      super();
      this.hasAnimated = false;
      this.observer = null;
      this.rafId = null;
      this.onCtaClick = this.onCtaClick.bind(this);
    }

    connectedCallback() {
      this.section = this.closest(".m-trust-section");
      this.chart = this.querySelector("[data-ts-chart]");
      this.bars = this.querySelectorAll("[data-ts-bar]");
      this.errorBars = this.querySelectorAll("[data-ts-error]");
      this.duration = Number(this.dataset.duration) || 1100;
      this.ctaButtons = this.querySelectorAll("[data-ts-scroll-cta]");

      this.ctaButtons.forEach((button) => {
        button.addEventListener("click", this.onCtaClick);
      });

      this.resetBars();

      if (this.prefersReducedMotion()) {
        this.animate(true);
        return;
      }

      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.animate(false);
                this.observer.disconnect();
              }
            });
          },
          { rootMargin: "0px 0px -12% 0px", threshold: 0.2 }
        );
        this.observer.observe(this.chart || this);
      } else {
        this.animate(false);
      }

      if (window.Shopify && Shopify.designMode) {
        this._onSectionLoad = (event) => {
          if (event.target && event.target.contains(this)) {
            this.hasAnimated = false;
            this.classList.remove("is-animated");
            if (this.section) this.section.classList.remove("is-animated");
            this.resetBars();
            this.animate(this.prefersReducedMotion());
          }
        };
        document.addEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.ctaButtons.forEach((button) => {
        button.removeEventListener("click", this.onCtaClick);
      });
      if (this._onSectionLoad) {
        document.removeEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    onCtaClick(event) {
      const target =
        document.querySelector(".m-main-product--info") ||
        document.querySelector("product-info[id^='MainProduct']");

      if (!target) return;

      event.preventDefault();

      const header = document.querySelector("sticky-header, .m-header, #shopify-section-header");
      const offset = header ? header.getBoundingClientRect().height : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - offset - 12;

      window.scrollTo({
        top: Math.max(top, 0),
        behavior: this.prefersReducedMotion() ? "auto" : "smooth",
      });
    }

    resetBars() {
      this.bars.forEach((bar) => {
        bar.style.height = "0";
      });
      this.errorBars.forEach((errorBar) => {
        errorBar.style.bottom = "0";
      });
    }

    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    updateErrorBars(progressByBar) {
      this.bars.forEach((bar, index) => {
        const errorBar = bar.parentElement
          ? bar.parentElement.querySelector("[data-ts-error]")
          : null;
        if (!errorBar) return;
        const target = Number(bar.dataset.targetPct) || 0;
        const current = target * (progressByBar[index] || 0);
        const isEmpty = bar.classList.contains("m-trust-section__bar--empty");
        // Bottom of the upright T touches the bar top.
        // Bars are shifted down 1px via CSS translateY, so subtract that for non-empty bars.
        if (isEmpty || current <= 1) {
          errorBar.style.bottom = "0px";
        } else {
          errorBar.style.bottom = `calc(${current}% - 1px)`;
        }
      });
    }

    animate(instant) {
      if (this.hasAnimated && !instant) return;
      this.hasAnimated = true;

      requestAnimationFrame(() => {
        this.classList.add("is-animated");
        if (this.section) this.section.classList.add("is-animated");

        if (instant) {
          this.bars.forEach((bar) => {
            bar.style.height = "var(--ts-target-height)";
          });
          this.updateErrorBars(Array.from(this.bars).map(() => 1));
          return;
        }

        this.runTimeline(this.duration);
      });
    }

    runTimeline(duration) {
      const start = performance.now();
      const barTargets = Array.from(this.bars).map((bar, index) => ({
        bar,
        target: Number(bar.dataset.targetPct) || 0,
        delay: Math.min(index * 0.05, 0.25),
      }));

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const progressByBar = [];

        barTargets.forEach((item, index) => {
          const local = Math.min(
            Math.max((progress - item.delay) / (1 - item.delay), 0),
            1
          );
          const eased = this.easeOutCubic(local);
          progressByBar[index] = eased;
          item.bar.style.height = `${item.target * eased}%`;
        });

        this.updateErrorBars(progressByBar);

        if (progress < 1) {
          this.rafId = requestAnimationFrame(tick);
        } else {
          this.bars.forEach((bar) => {
            bar.style.height = "var(--ts-target-height)";
          });
          this.updateErrorBars(Array.from(this.bars).map(() => 1));
        }
      };

      this.rafId = requestAnimationFrame(tick);
    }
  }

  customElements.define("m-trust-section", MTrustSection);
}
